import * as React from 'react'
import { cn } from '../../lib/utils'

type FieldControlProps = {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: React.AriaAttributes['aria-invalid']
  'aria-required'?: React.AriaAttributes['aria-required']
}

export type FieldProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  id: string
  label: React.ReactNode
  helpText?: React.ReactNode
  error?: React.ReactNode
  required?: boolean
  children: React.ReactElement<FieldControlProps>
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      id,
      label,
      helpText,
      error,
      required = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const controlId = children.props.id ?? id
    const helpId = helpText ? `${controlId}-help` : undefined
    const errorId = error ? `${controlId}-error` : undefined
    const describedBy = [
      children.props['aria-describedby'],
      helpId,
      errorId,
    ]
      .filter(Boolean)
      .join(' ')

    const control = React.cloneElement(children, {
      id: controlId,
      'aria-describedby': describedBy || undefined,
      'aria-invalid': error ? true : children.props['aria-invalid'],
      'aria-required': required || children.props['aria-required'] || undefined,
    })

    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-[var(--field-gap)]', className)}
        {...props}
      >
        <label
          htmlFor={controlId}
          className="text-[length:var(--field-label-size)] font-semibold text-[var(--field-label-text)]"
        >
          {label}
          {required && (
            <span
              aria-hidden="true"
              className="ml-1 text-[var(--field-error-text)]"
            >
              *
            </span>
          )}
        </label>
        {control}
        {helpText && (
          <p
            id={helpId}
            className="m-0 text-[length:var(--field-help-size)] text-[var(--field-help-text)]"
          >
            {helpText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className="m-0 text-[length:var(--field-help-size)] font-medium text-[var(--field-error-text)]"
          >
            {error}
          </p>
        )}
      </div>
    )
  },
)
Field.displayName = 'Field'
